import { createApi } from '@reduxjs/toolkit/query/react';
import { createBackendBaseQuery, transformApiError } from '../../utils/createAuthBaseQuery';
import { API_URLS } from '../../constants/apiUrl';

export interface MotorList {
  id: string;
  region: string;
  motorType: string;
  articleNumber: number;
  supplier: string;
  supplierDesignation: string;
  mechanicalDocument: string;
  electricalDocumentNumber: string;
  powerKw: string;
  powerHp: string;
  voltageV: string;
  active: boolean;
}

export interface MotorListResponse {
  totalResult: number;
  activeResult: number;
  motors: MotorList[];
}

export interface FetchMotorListParams {
  page?: number;
  limit?: number;
  sort?: keyof MotorList;
  order?: 'asc' | 'desc';
  [key: string]: string | number | undefined;
}

export interface PatchOperation {
  op: string;
  path: string;
  value: string | number | boolean | null;
}

// Payload for creating a motor's mechanical data via POST api/Motor/MechanicalData.
export interface MechanicalDataPayload {
  id: number;
  motorInvestigationFlowId: number;
  motorInvestigationFlowAlternativeId: number;
  isInvestigationInternal: boolean;
  isInvestigationInProgress: boolean;
  isOldMotorData: boolean;
  acMotorNumber: number;
  mechanicalDrawingNumber: string;
  mainDimension3d: string;
  motorMountingTypeMasterId: number;
  frameSize: string;
  shaftDiameterMm: number;
  shaftLengthMm: number;
  keyLengthMm: number;
  keyWidthMm: number;
  shaftHeightMm: number;
  shaftToGbHousingDistanceMm: number;
  centerHeightOfGbMm: number;
  gbMountingPositionsMm: number;
  flangePcdMm: number;
  flangePcdHolesAngle: number;
  flangePcdHolesDiameterMmPerMetricThread: string;
  numberOfHolesOnFlangePcd: number;
  overallLengthMm: number;
  overallHeightMm: number;
  supportBoltPositionMm: number;
  supportBoltSizeMetricThreadOrMm: string;
  distanceBetweenSupportBoltSideView: number;
  distanceBetweenSupportBoltFrontView: number;
  baffleDiameterMm: number;
  mainTerminalBoxAngle: string;
  gearboxHousing: number;
  createdAtUtc?: string;
  createdBy?: string;
  updatedAtUtc?: string;
  updatedBy?: string;
}

const BASE_URL = process.env.REACT_APP_MOTER_SERVICE_API_BASE_URL as string;

if (!BASE_URL) {
  throw new Error('REACT_APP_MOTER_SERVICE_API_BASE_URL environment variable is not set');
}

// Create the base query using the reusable factory
const baseQuery = createBackendBaseQuery(BASE_URL);

export const motorListApiSlice = createApi({
  reducerPath: 'motorListApi',
  baseQuery: baseQuery,
  tagTypes: ['Motors'],
  endpoints(builders) {
    return {
      fetchMotorList: builders.query<MotorListResponse, FetchMotorListParams | void>({
        query: (params) => {
          let queryString = '';
          if (params) {
            const usp = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                usp.append(key, String(value));
              }
            });
            queryString = usp.toString() ? `?${usp.toString()}` : '';
          }
          return {
            url: `${API_URLS.MOTOR_LIST.SEARCH}${queryString}`,
            method: 'GET',
          };
        },
        providesTags: ['Motors'],
        transformErrorResponse: transformApiError,
      }),
      // Archive (deactivate) a motor and its related investigations
      archiveMotor: builders.mutation<boolean, string>({
        query: (motorId) => ({
          url: `${API_URLS.MOTOR_LIST.ARCHIVE}?id=${motorId}`,
          method: 'POST',
        }),
        invalidatesTags: ['Motors'],
        transformErrorResponse: transformApiError,
      }),
      // UnArchive (activate) a motor
      unArchiveMotor: builders.mutation<boolean, string>({
        query: (motorId) => ({
          url: `${API_URLS.MOTOR_LIST.UNARCHIVE}?id=${motorId}`,
          method: 'POST',
        }),
        invalidatesTags: ['Motors'],
        transformErrorResponse: transformApiError,
      }),
      // Patch (partially update) a motor's electrical data using JSON Patch operations
      patchMotorElectricalData: builders.mutation<
        { id: number; status: number },
        { id: string; patchData: PatchOperation[] }
      >({
        query({ id, patchData }) {
          return {
            url: `${API_URLS.MOTOR_LIST.ELECTRICAL_DATA}?id=${id}`,
            method: 'PATCH',
            body: patchData,
            headers: {
              'Content-Type': 'application/json-patch+json',
              Accept: 'application/json',
            },
          };
        },
        invalidatesTags: ['Motors'],
        transformErrorResponse: transformApiError,
      }),
      // Create a motor's mechanical data record
      postMotorMechanicalData: builders.mutation<number, MechanicalDataPayload>({
        query(payload) {
          return {
            url: API_URLS.MOTOR_LIST.MECHANICAL_DATA,
            method: 'POST',
            body: payload,
          };
        },
        invalidatesTags: ['Motors'],
        transformErrorResponse: transformApiError,
      }),
      // Patch (partially update) a motor's mechanical data using JSON Patch operations
      patchMotorMechanicalData: builders.mutation<
        { id: number; status: number },
        { id: number; patchData: PatchOperation[] }
      >({
        query({ id, patchData }) {
          return {
            url: `${API_URLS.MOTOR_LIST.MECHANICAL_DATA}?id=${id}`,
            method: 'PATCH',
            body: patchData,
            headers: {
              'Content-Type': 'application/json-patch+json',
              Accept: 'application/json',
            },
          };
        },
        invalidatesTags: ['Motors'],
        transformErrorResponse: transformApiError,
      }),

      // Delete a Motor List data.
      // returns true if the deletion was successful, false otherwise.
       deleteMotorListData: builders.mutation<boolean, string>({
        query: (investigationId) => ({
          url: `${API_URLS.MOTOR_LIST.DELETE_DATA}?id=${investigationId}`,
          method: 'POST',
        }),
        invalidatesTags: ['Motors' ],
        transformErrorResponse: transformApiError,
      }),
    };
  },
});

export const {
  useFetchMotorListQuery,
  useLazyFetchMotorListQuery,
  useArchiveMotorMutation,
  useUnArchiveMotorMutation,
  usePatchMotorElectricalDataMutation,
  usePostMotorMechanicalDataMutation,
  usePatchMotorMechanicalDataMutation,
  useDeleteMotorListDataMutation,
} = motorListApiSlice;
